import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./Product.css";
import axiosInstance from "../../../Config/axiosInstance";

import { Box, Typography, Chip } from '@mui/material';
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

import AddProduct from "./AddProduct";
import EditProduct from "./EditProduct";
import NotAuthorized from "../../../Components/ProtectedRoute/NotAuthorized";

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului:", e);
    return url;
  }
};

const ProductList = ({ canCreate, canEdit, canDelete }) => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fecthProduct = async () => {
      try {
        const response = await axiosInstance.get("/product/");
        
        //campul nr pentru fiecare rand
        const dataWithNr = response.data.map((item, index) => ({
          ...item, 
          nr: response.data.length - index
        }));
        
        setRows(dataWithNr);
      } catch (error) {
        console.error("Eroare la preluarea Product: ", error);
      }
    };
    fecthProduct();
  }, [])

  const activeCount = rows.filter(row => row.status === 'ACTIVE').length;
  const inactiveCount = rows.filter(row => row.status === 'INACTIVE').length;
  const totalCount = rows.length;

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/product/${selectedProductId}/`);
      setRows((prevRows) => {
        //elimina randul sters
        const filteredRows = prevRows.filter((row) => row.id !== selectedProductId);

        //recalculeaza nr pentru toate randurile ramase
        const updatedRows = filteredRows.map((item, index) => ({
          ...item,
          nr: filteredRows.length-index
        }));
        
        return updatedRows;
      })
    } catch (error) {
      const msg = error.response?.data?.detail || "An error ocurred.";
      setErrorMessage(msg);
      setErrorDialogOpen(true);
    } finally {
      handleCloseDialog();
    }
  }

  const handleOpenDialog = (id) => {
    setSelectedProductId(id);
    setOpenDialog(true);
  }

  const handleCloseDialog = (id) => {
    setOpenDialog(false);
    setSelectedProductId(null);
  }

  //functie pentru a afisa statusul cu culori diferite
  const renderStatus = (params) => {
    const status = params.value;
    return (
      <Chip
        label={status}
        size="small"
        className={`status-chip ${
          status === 'ACTIVE' ? 'status-chip-active' : 'status-chip-inactive'
        }`}
      />
    );
  };

  const columns = [
    { field: "nr", headerName: "Nr. Article", minWidth: 120, flex: 1, headerClassName: "bold-header" },
    { field: "title", headerName: "Title", minWidth: 250, flex: 1, headerClassName: "bold-header" },
    { field: "material_name", headerName: "Quality of material", minWidth: 160, flex: 1, headerClassName: "bold-header" },
    { field: "dimenssions_name", headerName: "Dimension", minWidth: 170, flex: 1, headerClassName: "bold-header" },
    { field: "subcategory_name", headerName: "Subcategory", minWidth: 180, flex: 1, headerClassName: "bold-header" },
    { field: "color", headerName: "Color", minWidth: 120, flex: 1, headerClassName: "bold-header" },
    { field: "description", headerName: "Description", minWidth: 200, flex: 1, headerClassName: "bold-header" },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      headerClassName: "bold-header",
      renderCell: renderStatus
    }, 
    ...(canEdit || canDelete ? [
      {
        field: "actions", headerName: "Action", minWidth: 120, flex: 1, headerClassName: "bold-header", headerAlign: "center",
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              width: "100%",
              height: "100%",
            }}
          >
            {canEdit &&
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/product/edit/${params.row.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            }
            {canDelete &&
              <IconButton
                size="small"
                color="error"
                onClick={() => handleOpenDialog(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }
          </div>
        )
      }
    ] : [])
  ];

  return (
    <>
      <div className="product-page">
        <div className="header-container">
          <h2>PRODUCTS</h2>

          <div className="product-actions">
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/product/create")}
              >
                Create product
              </Button>
            )}
          </div>
        </div>

        <div className="stats-container">
            <div className="stat-card stat-total">
              Total Products&nbsp; 
              <span className="stat-badge">{totalCount}</span>
            </div>

            <div className="stat-card stat-active">
              Active Products&nbsp;
              <span className="stat-badge">{activeCount}</span>
            </div>

            <div className="stat-card stat-inactive">
              Inactive Products&nbsp;
              <span className="stat-badge">{inactiveCount}</span>
            </div>
        </div>

        <Box className="product-box-subcontainer product-page product-datagrid">
          <div className="product-data-grid-container full-width-height custom-product-datagrid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="confirm-dialog-title"
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this product?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent>
            <Typography>{errorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );

}

const Product = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
  const permissions = storedPermissions?.pages || [];

  const hasPermission = (path, checkPermission) => {
    const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
    return pagePermission;
  };

  const canCreate = hasPermission('/product', (page) => page.can_create);
  const canEdit = hasPermission('/product', (page) => page.can_edit);
  const canDelete = hasPermission('/product', (page) => page.can_delete);
  return (
    <Routes>
      <Route path="/" element={<ProductList canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />} />
      <Route path="create" element={canCreate ? <AddProduct /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <EditProduct /> : <NotAuthorized />} />
    </Routes>
  );
};

export default Product;